/**
 * *********************************************************************************************************************
 * Responsibility - fetches charges and/ or invoices and provides the necessary functionality to keep state of the Ids
 * to pay for. By default, it fetches both charges and invoices but can be configured to fetch only one or the other
 * via props.
 * **********
 *
 * @format
 */

/** @format */

import React, {useState, useEffect} from 'react';
import update from 'immutability-helper';
import SuperAgent from 'superagent';
import Moment from 'moment/moment';

export const useChargesAndInvoices = ({
    shouldFetchCharges = true,
    shouldFetchInvoices = true
} = {}) => {
    const [invoices, setInvoices] = useState({carts: [], limit: 0, isNet30: false});
    const [isLoading, setIsLoading] = useState({charges: true, invoices: true});
    const [showInvoicesModal, setShowInvoicesModal] = useState(false);
    const [showChargesModal, setShowChargesModal] = useState(false);
    const [payMultipleList, setPayMultipleList] = useState([]);
    const [isPayingSingle, setIsPayingSingle] = useState(false);
    const [charges, setCharges] = useState([]);
    const [error, setError] = useState(null);
    const [dues, setDues] = useState({
        charges: 0,
        invoices: 0,
        total: 0
    });
    const [pastDues, setPastDues] = useState({
        invoices: 0
    });

    const fetchCharges = async () => {
        try {
            const {
                body: {chargeBatches}
            } = await SuperAgent.get('/account/charge-batches/unpaid').set(
                'accept',
                'application/json'
            );

            setIsLoading(prev => ({...prev, charges: false}));
            setCharges(chargeBatches);
        } catch (error) {
            setError(error);
        }
    };

    const fetchInvoices = async () => {
        try {
            const {body} = await SuperAgent.get('/account/carts/unpaid').set(
                'accept',
                'application/json'
            );

            setIsLoading(prev => ({...prev, invoices: false}));
            setInvoices(body);
        } catch (error) {
            setError(error);
        }
    };

    const addTotalDues = () => {
        const chargesTotal = charges.reduce(
            (total, item) => parseInt(total) + parseInt(item.total),
            0
        );
        const invoicesTotal = invoices.carts.reduce(
            (total, item) => total + (parseInt(item.total) - parseInt(item.partial_payments)),
            0
        );

        setDues({
            total: chargesTotal + invoicesTotal,
            invoices: invoicesTotal,
            charges: chargesTotal
        });
    };

    const addPastDues = () => {
        const pastDueAmount = invoices.carts
            .filter(i => Moment(i.shipped_time).add(30, 'days') < Moment())
            .reduce((total, item) => total + (item.total - item.partial_payments), 0);

        setPastDues({invoices: pastDueAmount});
    };

    useEffect(() => {
        if (shouldFetchCharges) fetchCharges();
        if (shouldFetchInvoices) fetchInvoices();
    }, []);

    useEffect(() => {
        addTotalDues();
        addPastDues();
    }, [charges, invoices]);

    const handleAddItem = id => {
        setPayMultipleList(prev => [...prev, id]);
    };

    const handleRemoveItem = id => {
        const itemIndex = payMultipleList.indexOf(id);

        const updateTarget = update(payMultipleList, {$splice: [[itemIndex, 1]]});
        setPayMultipleList(updateTarget);
    };

    const handleToggleModal = (target, cartId) => {
        setIsPayingSingle(!!cartId);

        // add all charges and invoices to by default
        if (target === 'charges') {
            if (!cartId) setPayMultipleList(charges.map(item => item.id));
            else setPayMultipleList([cartId]);
            setShowChargesModal(prev => !prev);
        } else if (target === 'invoices') {
            if (!cartId) setPayMultipleList(invoices.carts.map(item => item.id));
            else setPayMultipleList([cartId]);
            setShowInvoicesModal(prev => !prev);
        }
    };

    return {
        isLoading: isLoading.charges && isLoading.invoices,
        handleToggleModal,
        showInvoicesModal,
        showChargesModal,
        handleRemoveItem,
        payMultipleList,
        isPayingSingle,
        handleAddItem,
        invoices,
        pastDues,
        charges,
        error,
        dues
    };
};
